import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Injectable, Optional, Injector, Inject } from '@angular/core';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import {
  L10nConfig, L10nLoader, L10nProvider, L10nTranslationLoader, L10nTranslationService,
  L10nMissingTranslationHandler, L10nStorage, L10nLocale, L10nUserLanguage
} from '@snack-dev/angular-l10n';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CookiesService } from './cookies/cookies.service';
import { UtilsService } from './shared/services/utils.service';

export const l10nProvider = { name: 'app', asset: 'assets/i18n', options: { version: '1.0.2' } };

export const englishLocale: L10nLocale = { language: 'en', currency: 'USD' };
export const spanishLocale: L10nLocale = { language: 'es', currency: 'USD' };
export const germanLocale: L10nLocale = { language: 'de', currency: 'USD' };
export const portugueseLocale: L10nLocale = { language: 'pt-br', currency: 'USD' };

export const l10nLocales: any[] = [
  { locale: englishLocale, dir: 'ltr', text: 'English' },
  { locale: spanishLocale, dir: 'ltr', text: 'Spanish' },
  { locale: portugueseLocale, dir: 'ltr', text: 'Portuguese (Brazil)' },
  { locale: germanLocale, dir: 'ltr', text: 'German' }
];
export const l10nConfig: L10nConfig = {
  format: 'language-region',
  providers: [l10nProvider],
  cache: true,
  defaultRouting: true,
  fallback: true,
  keySeparator: '.',
  defaultLocale: englishLocale,
  schema: l10nLocales
};

export function initL10n(l10nLoader: L10nLoader): () => Promise<void> {
  return () => l10nLoader.init();
}

/**
 * Loads translation files through HTTP.
 */
@Injectable() export class HttpTranslationLoader implements L10nTranslationLoader {

  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  private responseCache = new Map();

  constructor(@Optional() private http: HttpClient) { }

  public get(language: string, provider: L10nProvider): Observable<{ [key: string]: any }> {
    const cache = this.responseCache.get(language);
    if (!cache) {
      let file = language;
      if (file === 'pt' || file === 'pt-BR') {
        file = 'pt-br';
      }
      let url = environment.frontUrl + `${provider.asset}/${file}.json`;
      const options = {
        headers: this.headers,
        params: new HttpParams().set('v', provider.options.version)
      };
      this.responseCache.set(language, this.http.get(url, options).pipe(shareReplay()));
    }
    return this.responseCache.get(language);
  }
}

@Injectable() export class StoreTranslationHandler implements L10nStorage {

  private cookieKey: string = '_icl_current_language';

  constructor(
    private cookieService: CookiesService,
    private utils: UtilsService,
    @Optional() @Inject(REQUEST) protected request: Request
  ) { }

  read(): Promise<L10nLocale> {
    return new Promise<L10nLocale>((resolve) => {
      try {
        let path: string = '';
        if (this.utils.isBrowser) {
          path = window.location.pathname;
        } else if (this.request) {
          path = this.request.url;
        }

        let locale = englishLocale;
        if (path?.startsWith('/es/')) {
          locale = spanishLocale;
        }
        if (path?.startsWith('/de/')) {
          locale = germanLocale;
        }
        if (path?.startsWith('/pt-br/')) {
          locale = portugueseLocale;
        }
        this.cookieService.put(this.cookieKey, locale.language);
        resolve(locale);
      } catch (e) {
        resolve(englishLocale);
      }
    });
  }

  write(locale: L10nLocale): Promise<void> {
    return new Promise<void>((resolve) => {
      this.cookieService.put(this.cookieKey, locale.language);
      resolve();
    });
  }

}

/**
 * Handles missing translation keys. It's imperative to check if the locale to use has the key we
 * are about to retrieve, because (translation.translate) internally calls the MissingTranslationHandler
 * so we would be stuck in an infinite loop if we didn't.
 */
@Injectable() export class MissingTranslationHandler implements L10nMissingTranslationHandler {

  private translation: L10nTranslationService;

  constructor(@Optional() private injector: Injector) { }

  public handle(key: string, params?: any): string | any {
    this.translation = this.injector.get(L10nTranslationService);
    if (this.translation.has(key, 'en')) {
      const fallback = this.translation.translate(key, params ?? null, 'en');
      if (!environment.production) {
        console.warn('Translation for lang key: "' + key + '" is missing, using fallback: ' + fallback);
      }
      return fallback;
    } else {
      if (!environment.production) {
        console.error('Fallback for lang key: "' + key + '" is missing.');
      }
    }
    return key;
  }

}

/**
 * Preloads the English language file as a fallback for missing keys.
 */
export function l10nPreload(translation: L10nTranslationService, translationLoader: L10nTranslationLoader): () => Promise<void> {
  return () => new Promise((resolve) => {
    translationLoader.get('en', l10nProvider)
      .subscribe({
        next: (data) => translation.addData(data, 'en'),
        complete: () =>
          resolve()
      });
  });
}


@Injectable() export class UserLanguage implements L10nUserLanguage {

  constructor(
    private utils: UtilsService,
    @Optional() @Inject(REQUEST) protected request: Request
  ) { }

  get(): Promise<string | null> {
    return new Promise((resolve) => {
      let path: string = '';
      if (this.utils.isBrowser) {
        path = window.location.pathname;
      } else if (this.request) {
        path = this.request.url;
      }

      if (path?.startsWith('/es/')) {
        resolve('es');
      }
      if (path?.startsWith('/de/')) {
        resolve('de');
      }
      if (path?.startsWith('/pt-br/')) {
        resolve('pt-br');
      }
      resolve('en');
    });
  }

}
